import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { validateOAuth } from '@/react/portainer/generated-api/portainer/sdk.gen';
import { dispatchCacheRefreshEvent } from '@/portainer/services/http-request.helper';
import { isAxiosError } from '@/portainer/services/axios/utils/isAxiosError';
import { get, set } from '@/react/hooks/useLocalStorage';

import { resolveAuthError } from './authErrors';

export const OAUTH_STATE_KEY = 'LOGIN_STATE_UUID';
const OAUTH_LOGIN_BLOCK_KEY_PREFIX = 'oauth_login_block_code_';

export function useOAuthUri(
  baseUri: string | undefined,
  disabled: boolean
): string {
  const [stateUuid] = useState(() => uuidv4());

  useEffect(() => {
    set(OAUTH_STATE_KEY, stateUuid);
  }, [stateUuid]);

  return useMemo(
    () => (baseUri && !disabled ? `${baseUri}&state=${stateUuid}` : ''),
    [baseUri, disabled, stateUuid]
  );
}

export function useOAuthCallback() {
  const [initialParsed] = useState(() => parseOAuthCallback());
  const [authError, setAuthError] = useState(initialParsed?.error ?? '');
  const [isProcessing, setIsProcessing] = useState(!!initialParsed?.code);

  const oauthMutation = useMutation({
    mutationFn: (code: string) => validateOAuth({ body: { Code: code } }),
    onSuccess: () => {
      window.location.replace(
        window.location.origin + window.location.pathname + window.location.hash
      );
    },
    onError: (err: unknown, code: string) => {
      if (isAxiosError(err) && err.response?.status === 402) {
        storeOAuthLoginBlockForNonAdmin(code);
      }
      setAuthError(resolveAuthError(err, 'Unable to login via OAuth'));
      setIsProcessing(false);
    },
  });

  useEffect(() => {
    dispatchCacheRefreshEvent();
    if (!initialParsed?.code) return;
    oauthMutation.mutate(initialParsed.code);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { authError, isProcessing: isProcessing || oauthMutation.isLoading };
}

function parseOAuthCallback():
  | { code: string; error: '' }
  | { code: ''; error: string }
  | null {
  const params = new URLSearchParams(window.location.search);

  const state = params.get('state');
  const savedUUID = get<string>(OAUTH_STATE_KEY, '');
  const isStateValid = !!savedUUID && savedUUID === state;

  const providerError = params.get('error');
  if (providerError) {
    return {
      code: '',
      error: isStateValid
        ? params.get('error_description') || providerError
        : 'Invalid OAuth state, try again.',
    };
  }

  const code = params.get('code');
  if (!code || !state) return null;

  if (!isStateValid) {
    const error = isOAuthLoginBlockedForNonAdmin(code)
      ? 'Node limit for this license has been exceeded, please contact your administrator'
      : 'Invalid OAuth state, try again.';
    return { code: '', error };
  }
  return { code, error: '' };
}

function storeOAuthLoginBlockForNonAdmin(code: string) {
  localStorage.setItem(OAUTH_LOGIN_BLOCK_KEY_PREFIX + code, 'true');
}

function isOAuthLoginBlockedForNonAdmin(code: string) {
  return localStorage.getItem(OAUTH_LOGIN_BLOCK_KEY_PREFIX + code) === 'true';
}
